import type { Theme } from './themes';

/**
 * Theme → CSS custom property mapping. SINGLE SOURCE OF TRUTH.
 *
 * Consumed by both:
 *   - ThemeProvider's applyThemeToDOM() (runtime theme switching), and
 *   - themeInitScript() (the pre-paint <head> script in the root layout).
 *
 * Keeping one list is deliberate: the two previously drifted apart — the inline
 * script omitted --color-t-section-light / --color-t-section-alt that the
 * provider set, so those two properties were undefined until hydration.
 */
const VAR_MAP: ReadonlyArray<readonly [cssVar: string, themeKey: keyof Theme]> = [
  ['--color-t-primary', 'primary'],
  ['--color-t-primary-light', 'primaryLight'],
  ['--color-t-primary-dark', 'primaryDark'],
  ['--color-t-cta', 'cta'],
  ['--color-t-cta-hover', 'ctaHover'],
  ['--color-t-cta-text', 'ctaText'],
  ['--color-t-surface', 'surface'],
  ['--color-t-section-light', 'sectionLight'],
  ['--color-t-section-alt', 'sectionAlt'],
  ['--color-t-heading', 'heading'],
  ['--color-t-body', 'body'],
  ['--color-t-accent', 'accent'],
  ['--t-gradient', 'gradient'],
] as const;

/** localStorage key holding the visitor's chosen theme. */
export const THEME_STORAGE_KEY = 'op-theme';

/** Resolve a theme into the [cssVar, value] pairs to set on document.documentElement. */
export function themeCssVars(theme: Theme): Array<[string, string]> {
  return VAR_MAP.map(([cssVar, key]) => {
    const value = theme[key];
    // `gradient` is the only optional field; `none` keeps var() from falling back
    // to an inherited/undefined value on themes that define no gradient.
    return [cssVar, typeof value === 'string' && value ? value : cssVar === '--t-gradient' ? 'none' : ''];
  });
}

/** Escape a JSON literal so it can never terminate the inlining <script> element. */
function safeJson(value: unknown): string {
  return JSON.stringify(value).replace(/</g, '\\u003c');
}

/**
 * A synchronous, inline <head> script that applies the theme's custom properties
 * BEFORE first paint.
 *
 * Why this must ALWAYS apply a theme (and not only when localStorage has one):
 * the site renders ~900 inline styles of the form `style="color:var(--color-t-primary)"`.
 * Until those custom properties exist on the document, every one of them resolves
 * to nothing and the page paints unstyled black-on-white. The previous version
 * was gated on `if (localStorage.getItem('op-theme'))`, so every first-time
 * visitor (and anyone with cleared storage / incognito) got no properties at all
 * until ThemeProvider's post-hydration effect ran — a full-page flash of unstyled
 * content on every fresh document load. Client-side navigations were unaffected
 * because the properties were already on the document by then.
 *
 * @param fallback theme applied when the visitor has no saved theme.
 */
export function themeInitScript(fallback: Theme): string {
  return (
    `(function(){try{` +
    `var M=${safeJson(VAR_MAP.map(([cssVar, key]) => [cssVar, key]))},D=${safeJson(fallback)};` +
    `var d=D,t=null;` +
    `try{t=localStorage.getItem(${safeJson(THEME_STORAGE_KEY)})}catch(e){}` +
    `if(t){try{var p=JSON.parse(t);if(p&&p.primary)d=p}catch(e){}}` +
    `var r=document.documentElement.style;` +
    `for(var i=0;i<M.length;i++){` +
    `var n=M[i][0],v=d[M[i][1]];` +
    `if(!v&&n==='--t-gradient')v='none';` +
    `if(v)r.setProperty(n,v)}` +
    `}catch(e){}})()`
  );
}
